import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import type { components } from "../schema";

export type HistoryEntry = components["schemas"]["HistoryEntry"];

export type UseHistoryResult = UseQueryResult<HistoryEntry[], ApiError>;

// GET /playlines/{playlineId}/history を呼び、選択履歴（chosenAt 昇順）を返す query。
// playlineId が null / undefined の場合はクエリ自体を無効化する。
// useWorldlineScenes と同 pattern で実装し、cache key も同じ命名規則 (["history", playlineId]) を採用する。
export function useHistory(
  playlineId: string | null | undefined,
): UseHistoryResult {
  const client = useApiClient();

  return useQuery<HistoryEntry[], ApiError>({
    queryKey: ["history", playlineId],
    enabled: playlineId !== null && playlineId !== undefined,
    queryFn: async () => {
      // enabled: false の場合は呼ばれないが、型を狭めるための保険的なガード。
      if (playlineId === null || playlineId === undefined) {
        throw new ApiError(0, {
          type: "about:blank",
          title: "Invalid state",
          status: 0,
          detail: "useHistory queryFn invoked without playlineId",
        });
      }

      const { data, error } = await client.GET(
        "/playlines/{playlineId}/history",
        {
          params: { path: { playlineId } },
        },
      );

      if (error !== undefined) {
        throw new ApiError(
          typeof (error as { status?: number }).status === "number"
            ? (error as { status: number }).status
            : 0,
          error as unknown as ApiError["problem"],
        );
      }
      if (data === undefined) {
        throw new ApiError(500, {
          type: "about:blank",
          title: "Empty response",
          status: 500,
          detail: "getHistory returned no data",
        });
      }
      return data;
    },
  });
}
